import { databaseConfig } from '../../config/database';

/** Stock count API types (camelCase, dates as ISO strings from JSON). */

export type CountType = 'full' | 'cycle' | 'spot';
export type CountMode = 'total_only' | 'dual' | 'floor_only';
export type CountStatus = 'draft' | 'in_progress' | 'review' | 'closed' | 'cancelled';
export type LineStatus = 'pending' | 'counted' | 'recount' | 'approved' | 'skipped';
export type CountSource = 'pos' | 'web';

export interface Progress {
  total: number;
  counted: number;
  pending: number;
  recount: number;
  percent: number;
}

export interface SessionVariance {
  shortageLines: number;
  surplusLines: number;
  netUnits: number;
  highVarianceLines: number;
}

export interface StockCountSession {
  id: string;
  visibleId: string;
  branchId: string;
  type: CountType;
  countMode: CountMode;
  status: CountStatus;
  categoryIds: string[];
  productIds: string[];
  notes: string | null;
  thresholdUnits: number;
  thresholdPercent: number;
  createdByUserId: string;
  createdByName: string;
  approvedByUserId: string | null;
  approvedByName: string | null;
  approveComment: string | null;
  createdAt: string;
  startedAt: string | null;
  submittedAt: string | null;
  closedAt: string | null;
  progress: Progress;
  variance: SessionVariance;
}

export type SessionSummary = StockCountSession;

export interface StockCountLine {
  id: string;
  sessionId: string;
  branchId: string;
  productId: string;
  productName: string;
  sku: string;
  barcode: string | null;
  categoryId: string | null;
  imageUrl: string | null;
  theoreticalStock: number;
  theoreticalExposed: number;
  countedStock: number | null;
  countedExposed: number | null;
  varianceStock: number | null;
  varianceExposed: number | null;
  status: LineStatus;
  countedByUserId: string | null;
  countedByName: string | null;
  countedAt: string | null;
  source: CountSource | null;
}

export interface StockCountLinesMeta {
  total: number;
  page: number;
  limit: number;
  counted: number;
}

export interface StockCountReport {
  session: StockCountSession;
  lines: StockCountLine[];
  summary: Progress & SessionVariance;
}

export interface StockCountActivityItem {
  id: string;
  visibleId: string;
  branchId: string;
  productId: string;
  movementType: string;
  quantity: number;
  previousStock: number;
  newStock: number;
  reason: string;
  referenceId?: string;
  userId: string;
  createdAt: string;
}

export interface CreateStockCountPayload {
  branchId: string;
  type: CountType;
  countMode: CountMode;
  categoryIds?: string[];
  productIds?: string[];
  notes?: string;
  thresholdUnits?: number;
  thresholdPercent?: number;
}

export interface UpdateStockCountLinePayload {
  countedStock?: number;
  countedExposed?: number;
  markRecount?: boolean;
  source: CountSource;
}

export interface SubmitStockCountPayload {
  force?: boolean;
  reason?: string;
}

export interface ApproveStockCountPayload {
  lineIds?: string[];
  comment?: string;
  acknowledgeHighVariance?: boolean;
}

const baseUrl = databaseConfig.mongodb.apiBaseUrl;

export class StockCountApiError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'StockCountApiError';
    this.status = status;
    this.body = body;
  }
}

async function apiRequest<T>(
  path: string,
  accessToken: string,
  options: { method?: string; body?: string; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      (body as { message?: string | string[] }).message ?? response.statusText;
    throw new StockCountApiError(
      Array.isArray(message) ? message.join(', ') : String(message),
      response.status,
      body,
    );
  }

  return body as T;
}

export interface StockCountListMeta {
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export async function listBranchStockCounts(
  accessToken: string,
  branchId: string,
  params: { page?: number; limit?: number; status?: string; search?: string } = {},
): Promise<{
  sessions: StockCountSession[];
  meta: StockCountListMeta;
  active: StockCountSession | null;
}> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 100));
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.search) qs.set('search', params.search);
  const result = await apiRequest<{
    data: StockCountSession[];
    meta?: StockCountListMeta;
    active?: StockCountSession | null;
  }>(
    `/api/inventory/counts/branch/${encodeURIComponent(branchId)}?${qs.toString()}`,
    accessToken,
  );
  const sessions = result.data ?? [];
  return {
    sessions,
    meta: result.meta ?? {
      total: sessions.length,
      page: params.page ?? 1,
      limit: params.limit ?? 100,
      hasMore: false,
    },
    active: result.active ?? null,
  };
}

export async function createStockCount(
  accessToken: string,
  payload: CreateStockCountPayload,
): Promise<StockCountSession> {
  const result = await apiRequest<{ data: StockCountSession }>(
    '/api/inventory/counts',
    accessToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return result.data;
}

export async function startStockCount(
  accessToken: string,
  visibleId: string,
): Promise<StockCountSession> {
  const result = await apiRequest<{ data: StockCountSession }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/start`,
    accessToken,
    { method: 'POST' },
  );
  return result.data;
}

export async function getStockCount(
  accessToken: string,
  visibleId: string,
): Promise<StockCountSession> {
  const result = await apiRequest<{ data: StockCountSession }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}`,
    accessToken,
  );
  return result.data;
}

export async function getStockCountLines(
  accessToken: string,
  visibleId: string,
  params: { status?: string; q?: string; page?: number; limit?: number } = {},
): Promise<{ lines: StockCountLine[]; meta: StockCountLinesMeta }> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.page != null) qs.set('page', String(params.page));
  if (params.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const result = await apiRequest<{
    data: StockCountLine[];
    meta: StockCountLinesMeta;
  }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/lines${query ? `?${query}` : ''}`,
    accessToken,
  );
  return {
    lines: result.data ?? [],
    meta: result.meta ?? { total: 0, page: 1, limit: 50, counted: 0 },
  };
}

export async function updateStockCountLine(
  accessToken: string,
  visibleId: string,
  lineId: string,
  payload: UpdateStockCountLinePayload,
): Promise<{ line: StockCountLine; progress: Progress }> {
  const result = await apiRequest<{
    data: { line: StockCountLine; progress: Progress };
  }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/lines/${encodeURIComponent(lineId)}`,
    accessToken,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  return result.data;
}

export async function addStockCountLine(
  accessToken: string,
  visibleId: string,
  payload: { productId?: string; barcode?: string; sku?: string; q?: string },
): Promise<{ line: StockCountLine; created: boolean; progress: Progress }> {
  const result = await apiRequest<{
    data: { line: StockCountLine; created: boolean; progress: Progress };
  }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/lines`,
    accessToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return result.data;
}

export async function submitStockCount(
  accessToken: string,
  visibleId: string,
  payload: SubmitStockCountPayload = {},
): Promise<StockCountSession> {
  const result = await apiRequest<{ data: StockCountSession }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/submit`,
    accessToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return result.data;
}

export async function reopenStockCount(
  accessToken: string,
  visibleId: string,
): Promise<StockCountSession> {
  const result = await apiRequest<{ data: StockCountSession }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/reopen`,
    accessToken,
    { method: 'POST' },
  );
  return result.data;
}

export async function approveStockCount(
  accessToken: string,
  visibleId: string,
  payload: ApproveStockCountPayload,
): Promise<{
  session: StockCountSession;
  applied: number;
  skipped: number;
  movements: number;
}> {
  const result = await apiRequest<{
    data: {
      session: StockCountSession;
      applied: number;
      skipped: number;
      movements: number;
    };
  }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/approve`,
    accessToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return result.data;
}

export async function cancelStockCount(
  accessToken: string,
  visibleId: string,
): Promise<StockCountSession> {
  const result = await apiRequest<{ data: StockCountSession }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/cancel`,
    accessToken,
    { method: 'POST' },
  );
  return result.data;
}

export async function getStockCountReport(
  accessToken: string,
  visibleId: string,
): Promise<StockCountReport> {
  const result = await apiRequest<{ data: StockCountReport }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/report`,
    accessToken,
  );
  return result.data;
}

export async function getStockCountActivity(
  accessToken: string,
  visibleId: string,
): Promise<StockCountActivityItem[]> {
  const result = await apiRequest<{ data: StockCountActivityItem[] }>(
    `/api/inventory/counts/${encodeURIComponent(visibleId)}/activity`,
    accessToken,
  );
  return result.data ?? [];
}
